if (!customElements.get('product-family-picker')) {
  class ProductFamilyPicker extends HTMLElement {
    static getFormRank(label) {
      const text = (label || '').toLowerCase();
      if (/\b(capsules?|caps|caplets)\b/.test(text)) return 10;
      if (/\btablets?\b/.test(text)) return 20;
      if (/\b(liquid|syrup|shot|drink)\b/.test(text)) return 30;
      return 40;
    }

    static getWeightGrams(label) {
      const text = (label || '').toLowerCase();

      const multiPack = text.match(/(\d+)\s*x\s*(\d+(?:\.\d+)?)\s*(kg|g|ml)\b/);
      if (multiPack) {
        const count = parseInt(multiPack[1], 10);
        let amount = parseFloat(multiPack[2]);
        if (multiPack[3] === 'kg') amount *= 1000;
        return count * amount;
      }

      const kgMatch = text.match(/(\d+(?:\.\d+)?)\s*kg\b/g);
      if (kgMatch?.length) {
        const value = parseFloat(kgMatch[kgMatch.length - 1]);
        return Math.round(value * 1000);
      }

      const gMatches = [...text.matchAll(/(\d+(?:\.\d+)?)\s*g\b/g)];
      if (gMatches.length) {
        return Math.round(parseFloat(gMatches[gMatches.length - 1][1]));
      }

      const mlMatch = text.match(/(\d+(?:\.\d+)?)\s*ml\b/g);
      if (mlMatch?.length) {
        return Math.round(parseFloat(mlMatch[mlMatch.length - 1]));
      }

      const countMatch = text.match(/(\d+)\s*(?:capsules?|caps|caplets|tablets?|tabs|servings?|sachets?|softgels?|gummies|bars?)\b/);
      if (countMatch) {
        return parseInt(countMatch[1], 10);
      }

      return 999999;
    }

    static compareLabels(a, b) {
      const formA = ProductFamilyPicker.getFormRank(a);
      const formB = ProductFamilyPicker.getFormRank(b);
      if (formA !== formB) return formA - formB;

      const weightA = ProductFamilyPicker.getWeightGrams(a);
      const weightB = ProductFamilyPicker.getWeightGrams(b);
      if (weightA !== weightB) return weightA - weightB;

      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    }

    /**
     * Splits a sibling label into { flavor, size }, e.g.
     * "Chocolate Silk - 2kg" -> { flavor: "Chocolate Silk", size: "2kg" }
     * "60 Capsules" -> { flavor: "", size: "60 Capsules" }
     * Returns null when no trailing size token is found.
     */
    static parseLabel(label) {
      const text = (label || '').trim();
      if (!text) return null;

      const sizePattern = /^(.*?)[\s,([–—-]*((?:\d+\s*[xX]\s*)?\d+(?:[.,]\d+)?\s*(?:kg|g|grams?|mg|ml|cl|l|litres?|liters?|lbs?|oz|caps(?:ules)?|caplets|tabs?|tablets?|servings?|servs?|sachets?|softgels?|gummies|bars?|shots?))[\s.)\]]*$/i;
      const match = text.match(sizePattern);
      if (!match) return null;

      const size = match[2].replace(/\s+/g, ' ').trim();
      const flavor = match[1].replace(/[\s,([–—-]+$/, '').trim();
      return { flavor, size };
    }

    connectedCallback() {
      this.familyData = this.readFamilyData();
      this.labels = {
        flavour: this.dataset.labelFlavour || 'Flavour',
        size: this.dataset.labelSize || 'Size',
        defaultFlavour: this.dataset.labelDefaultFlavour || 'Original',
      };

      const swatches = this.querySelector('[data-family-options]');
      if (swatches) {
        this.initSwatches(swatches);
        return;
      }

      customElements.whenDefined('custom-select').then(() => {
        this.customSelect = this.querySelector('custom-select');
        this.nativeSelect = this.querySelector('.custom-select__native');

        this.sortDropdownOptions();
        this.syncButtonFromSelected();

        if (this.customSelect) {
          this.customSelect.addEventListener('change', (evt) => {
            this.syncButtonFromSelected();
            this.handleChange(evt);
          });
        }

        if (this.nativeSelect) {
          this.nativeSelect.addEventListener('change', this.handleNativeChange.bind(this));
        }

        this.loadRemainingSiblings();
      });
    }

    /**
     * Liquid can only read the first 250 products in a brand collection.
     * Later pages still belong to the same product line, so fetch them and
     * add any sibling the server render did not already include.
     */
    async loadRemainingSiblings() {
      const line = (this.dataset.productLine || '').trim().toLowerCase();
      const vendorHandle = (this.dataset.vendorHandle || '').trim();
      const vendorName = (this.dataset.vendor || '').trim().toLowerCase();
      if (!line || !vendorHandle || !this.customSelect) return;

      const listbox = this.customSelect.querySelector('.custom-select__listbox');
      if (!listbox) return;

      const seen = new Set(
        [...listbox.querySelectorAll('.custom-select__option')].map((option) => (
          (option.dataset.productUrl || option.dataset.value || '').split('?')[0]
        ))
      );
      const hideUnavailable = this.dataset.hideUnavailable !== 'false';
      let added = false;

      for (let page = 1; page <= 8; page += 1) {
        let products = [];
        try {
          const response = await fetch(`/collections/${vendorHandle}/products.json?limit=250&page=${page}`);
          if (!response.ok) break;
          const data = await response.json();
          products = data.products || [];
        } catch (e) {
          break;
        }

        products.forEach((product) => {
          if ((product.vendor || '').trim().toLowerCase() !== vendorName) return;
          if (ProductFamilyPicker.productLine(product.title) !== line) return;

          const url = `/products/${product.handle}`;
          if (seen.has(url)) return;

          const available = (product.variants || []).some((variant) => variant.available);
          if (hideUnavailable && !available) return;

          seen.add(url);
          this.appendFamilyOption({
            url,
            label: ProductFamilyPicker.optionLabel(product.title),
            image: ProductFamilyPicker.thumbUrl(product.images?.[0]?.src),
          });
          added = true;
        });

        if (products.length < 250) break;
      }

      if (!added) return;

      if (this.customSelect) {
        this.customSelect.options = this.customSelect.querySelectorAll('.custom-select__option');
      }
      this.sortDropdownOptions();
    }

    appendFamilyOption({ url, label, image }) {
      const listbox = this.customSelect?.querySelector('.custom-select__listbox');
      if (!listbox || !label) return;

      const option = document.createElement('li');
      option.className = 'custom-select__option product-family-select__option flex items-center gap-3 js-option';
      option.id = `${this.customSelect.id}-opt-${url.replace(/[^\w-]+/g, '-')}`;
      option.setAttribute('role', 'option');
      option.dataset.value = url;
      option.dataset.productUrl = url;
      option.dataset.label = label;
      if (image) option.dataset.imageUrl = image;

      if (image) {
        const thumb = document.createElement('span');
        thumb.className = 'product-family-select__thumb media relative shrink-0 pointer-events-none';
        const img = document.createElement('img');
        img.src = image;
        img.alt = label;
        img.width = 40;
        img.height = 40;
        img.loading = 'lazy';
        thumb.appendChild(img);
        option.appendChild(thumb);
      }

      const name = document.createElement('span');
      name.className = 'product-family-select__label pointer-events-none text-start';
      name.textContent = label;
      option.appendChild(name);
      listbox.appendChild(option);

      if (this.nativeSelect && ![...this.nativeSelect.options].some((item) => item.value === url)) {
        const native = document.createElement('option');
        native.value = url;
        native.textContent = label;
        this.nativeSelect.appendChild(native);
      }
    }

    static isSizeToken(word) {
      return /\d/.test(word) && /kg|mg|ml|\bg\b|g$|lb|oz|cap|tab|serv/i.test(word);
    }

    static productLine(title) {
      const normalized = ProductFamilyPicker.normalizeTitle(title);
      const parts = normalized.split(' - ');
      if (parts.length > 1) return parts[0].toLowerCase().trim();

      const words = normalized.toLowerCase().split(/\s+/).filter(Boolean);
      const line = [];
      for (const word of words) {
        if (ProductFamilyPicker.isSizeToken(word)) break;
        line.push(word);
      }
      if (line.length && line.length < words.length) return line.join(' ');
      return '';
    }

    static optionLabel(title) {
      const normalized = ProductFamilyPicker.normalizeTitle(title);
      const parts = normalized.split(' - ');
      if (parts.length > 1) return parts.slice(1).join(' - ').trim();

      const words = normalized.split(/\s+/).filter(Boolean);
      let size = '';
      const flavour = [];
      let seenSize = false;
      words.forEach((word) => {
        if (!seenSize && ProductFamilyPicker.isSizeToken(word)) {
          size = word;
          seenSize = true;
        } else if (seenSize) {
          flavour.push(word);
        }
      });
      if (seenSize && flavour.length) return `${flavour.join(' ')} - ${size}`;
      if (seenSize) return size;
      return normalized;
    }

    static normalizeTitle(title) {
      return (title || '')
        .replace(/\s[–—]\s/g, ' - ')
        .replace(/[–—]/g, ' - ')
        .replace(/,\s/g, ' - ')
        .trim();
    }

    static thumbUrl(src) {
      if (!src) return '';
      const base = src.replace(/([?&])width=\d+/, '$1').replace(/[?&]$/, '');
      return `${base}${base.includes('?') ? '&' : '?'}width=192`;
    }

    readFamilyData() {
      const payload = this.querySelector('script[data-family-data]');
      if (!payload) return null;

      try {
        const data = JSON.parse(payload.textContent);
        return Array.isArray(data) ? data : null;
      } catch (e) {
        return null;
      }
    }

    /**
     * Upgrades the server-rendered one-dimensional pill grid into
     * flavour swatches + size pills when every label parses cleanly.
     */
    initSwatches(root) {
      const entries = this.familyData;
      if (!entries || entries.length < 2) return;

      const parsed = entries.map((entry) => ({
        ...entry,
        parsed: ProductFamilyPicker.parseLabel(entry.label),
      }));

      if (parsed.some((entry) => !entry.parsed)) return;

      const flavorKey = (f) => (f || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const sizeKey = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

      const flavors = [];
      const sizes = [];
      parsed.forEach((entry) => {
        const fk = flavorKey(entry.parsed.flavor);
        const sk = sizeKey(entry.parsed.size);
        if (!flavors.some((f) => f.key === fk)) {
          flavors.push({ key: fk, name: entry.parsed.flavor || this.labels.defaultFlavour });
        }
        if (!sizes.some((s) => s.key === sk)) {
          sizes.push({ key: sk, name: entry.parsed.size });
        }
      });

      if (flavors.length < 2 && sizes.length < 2) return;

      sizes.sort((a, b) => ProductFamilyPicker.compareLabels(a.name, b.name));

      const current = parsed.find((entry) => entry.current) || parsed[0];
      const currentFlavorKey = flavorKey(current.parsed.flavor);
      const currentSizeKey = sizeKey(current.parsed.size);

      const findEntry = (fk, sk) => parsed.find((entry) => (
        flavorKey(entry.parsed.flavor) === fk && sizeKey(entry.parsed.size) === sk
      ));

      root.replaceChildren();

      if (flavors.length >= 2) {
        const grid = document.createElement('div');
        grid.className = 'product-family-swatches__grid product-family-swatches__grid--flavors';

        flavors.forEach((flavor) => {
          let target = findEntry(flavor.key, currentSizeKey);
          if (!target) {
            // No same-size sibling for this flavour: pick its closest size.
            const currentWeight = ProductFamilyPicker.getWeightGrams(current.parsed.size);
            target = parsed
              .filter((entry) => flavorKey(entry.parsed.flavor) === flavor.key)
              .sort((a, b) => (
                Math.abs(ProductFamilyPicker.getWeightGrams(a.parsed.size) - currentWeight)
                - Math.abs(ProductFamilyPicker.getWeightGrams(b.parsed.size) - currentWeight)
              ))[0];
          }
          if (!target) return;

          const isSelected = flavor.key === currentFlavorKey;
          const swatch = document.createElement('a');
          swatch.className = 'product-family-swatch'
            + (isSelected ? ' is-selected' : '')
            + (target.available === false ? ' is-unavailable' : '');
          swatch.href = target.url;
          if (isSelected) swatch.setAttribute('aria-current', 'page');
          swatch.title = flavor.name;
          swatch.setAttribute('aria-label', flavor.name);

          if (target.image) {
            const thumb = document.createElement('span');
            thumb.className = 'product-family-swatch__thumb';
            const img = document.createElement('img');
            img.src = target.image;
            img.alt = '';
            img.width = 192;
            img.loading = 'lazy';
            thumb.appendChild(img);
            swatch.appendChild(thumb);
          }

          const name = document.createElement('span');
          name.className = 'product-family-swatch__name';
          name.textContent = flavor.name;
          swatch.appendChild(name);

          grid.appendChild(swatch);
        });

        root.appendChild(this.buildRow(this.labels.flavour, current.parsed.flavor || this.labels.defaultFlavour, grid));
      }

      if (sizes.length >= 2 || flavors.length < 2) {
        const grid = document.createElement('div');
        grid.className = 'product-family-swatches__grid product-family-swatches__grid--sizes';

        sizes.forEach((size) => {
          const target = findEntry(currentFlavorKey, size.key);
          const isSelected = size.key === currentSizeKey;

          if (target) {
            const pill = document.createElement('a');
            pill.className = 'product-family-pill product-family-pill--size'
              + (isSelected ? ' is-selected' : '')
              + (target.available === false ? ' is-unavailable' : '');
            pill.href = target.url;
            if (isSelected) pill.setAttribute('aria-current', 'page');
            const label = document.createElement('span');
            label.className = 'product-family-pill__label';
            label.textContent = size.name;
            pill.appendChild(label);
            grid.appendChild(pill);
          } else {
            // This size doesn't exist for the current flavour.
            const pill = document.createElement('span');
            pill.className = 'product-family-pill product-family-pill--size is-disabled';
            pill.setAttribute('aria-disabled', 'true');
            const label = document.createElement('span');
            label.className = 'product-family-pill__label';
            label.textContent = size.name;
            pill.appendChild(label);
            grid.appendChild(pill);
          }
        });

        root.appendChild(this.buildRow(this.labels.size, current.parsed.size, grid));
      }
    }

    buildRow(name, value, grid) {
      const row = document.createElement('div');
      row.className = 'product-family-swatches__row';
      row.setAttribute('data-option-row', '');

      const header = document.createElement('div');
      header.className = 'product-family-swatches__header';

      const legend = document.createElement('span');
      legend.className = 'label product-family-swatches__legend';
      legend.append(`${name}: `);

      const valueEl = document.createElement('span');
      valueEl.className = 'option-selector__label-value';
      valueEl.textContent = value;
      legend.appendChild(valueEl);

      header.appendChild(legend);
      row.appendChild(header);
      row.appendChild(grid);
      return row;
    }

    /**
     * Keeps thumbnail + label in the button after custom-select updates.
     */
    syncButtonFromSelected() {
      if (!this.customSelect) return;

      const option = this.customSelect.querySelector('.custom-select__option[aria-selected="true"]');
      const btn = this.customSelect.querySelector('.custom-select__btn');
      if (!option || !btn) return;

      const label = option.dataset.label || option.querySelector('.product-family-select__label')?.textContent?.trim() || '';
      const imageUrl = option.dataset.imageUrl || '';
      const icon = btn.querySelector('.icon');
      const thumbSize = this.querySelector('.product-family-select__thumb img')?.getAttribute('width') || '40';

      const valueEl = document.createElement('span');
      valueEl.className = 'product-family-select__value flex items-center gap-3 min-w-0';

      if (imageUrl) {
        const thumbWrap = document.createElement('span');
        thumbWrap.className = 'product-family-select__thumb media relative shrink-0';
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.width = parseInt(thumbSize, 10);
        img.height = parseInt(thumbSize, 10);
        img.loading = 'lazy';
        thumbWrap.appendChild(img);
        valueEl.appendChild(thumbWrap);
      }

      const labelEl = document.createElement('span');
      labelEl.className = 'product-family-select__label text-start';
      labelEl.textContent = label;
      valueEl.appendChild(labelEl);

      btn.replaceChildren(valueEl);
      if (icon) btn.appendChild(icon);

      if (typeof this.customSelect.setButtonWidth === 'function') {
        this.customSelect.setButtonWidth();
      }
    }

    sortDropdownOptions() {
      if (!this.customSelect) return;

      const listbox = this.customSelect.querySelector('.custom-select__listbox');
      if (!listbox) return;

      const options = Array.from(listbox.querySelectorAll('.custom-select__option'));
      if (options.length < 2) return;

      options.sort((optionA, optionB) => {
        const labelA = optionA.dataset.label || optionA.textContent?.trim() || '';
        const labelB = optionB.dataset.label || optionB.textContent?.trim() || '';
        return ProductFamilyPicker.compareLabels(labelA, labelB);
      });

      options.forEach((option) => listbox.appendChild(option));

      if (this.nativeSelect) {
        const nativeOptions = Array.from(this.nativeSelect.options).filter((opt) => opt.value);
        nativeOptions.sort((optionA, optionB) => (
          ProductFamilyPicker.compareLabels(optionA.textContent?.trim() || '', optionB.textContent?.trim() || '')
        ));
        nativeOptions.forEach((option) => this.nativeSelect.appendChild(option));
      }
    }

    /**
     * @param {string} url
     */
    navigateTo(url) {
      if (!url) return;

      const targetPath = url.startsWith('http')
        ? new URL(url, window.location.origin).pathname
        : url.split('?')[0];

      if (window.location.pathname !== targetPath) {
        window.location.assign(url);
      }
    }

    /**
     * @param {CustomEvent} evt
     */
    handleChange(evt) {
      const url = evt.detail?.productUrl || evt.detail?.selectedValue;
      this.navigateTo(url);
    }

    /**
     * @param {Event} evt
     */
    handleNativeChange(evt) {
      this.navigateTo(evt.target.value);
    }
  }

  customElements.define('product-family-picker', ProductFamilyPicker);
}
